/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { nanoid } from "nanoid";
import {
  CSSProperties,
  DetailedHTMLProps,
  HTMLAttributes,
  useMemo,
} from "react";
import ReactTooltip from "react-tooltip";
import StartCampaignTagIcon from "../assets/icon/start-campaign-tag";
import { ICategoryType } from "../generated/Api";
import { customTooltip } from "../styles/general";
import { ITag } from "../types/TagTypes";
import IconBerserkerMail from "./Icon";

const Container = styled(motion.div)<{ type: string }>`
  background: ${(props) => (props.type === "user" ? "#fb972e19" : "#f8f8f8")} 0%
    0% no-repeat padding-box;
  border: 1px solid
    ${(props) => (props.type === "user" ? "#fb972e" : "#e0e0e0")};
  border-radius: 15px;
  opacity: 1;
  color: #fb972e;
  padding: 7px 12px;
  margin: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  cursor: default;
  width: fit-content;
  max-height: 32px;
  max-width: 90%;
  position: relative;

  .isStart {
    left: 0;
    top: -5px;
  }
  .isStop {
    right: 0;
    top: -5px;
  }
`;

const Icon = styled.div`
  margin-left: 10px;
  margin-top: 2px;
  cursor: pointer;
`;

const TextContainer = styled.div`
  text-overflow: ellipsis;
  max-width: 312px;
  overflow: hidden;
  white-space: nowrap;
`;

const TooltipText = styled.span`
  font-size: 14px;
  letter-spacing: 0px;
  color: #ffffff;
  text-align: center;
`;

const BadgeContainer = styled.div`
  position: absolute;

  img {
    -webkit-filter: drop-shadow(2px 1px 0 white) drop-shadow(-1px -1px 0 white);
    filter: drop-shadow(2px 1px 0 white) drop-shadow(-1px -1px 0 white);
  }
`;

interface Props
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  tag: ITag & { isStartTag?: boolean; isStopTag?: boolean };
  customStyle?: CSSProperties;
  isLayout?: boolean;
  tooltip?: string;
  disabled?: boolean;
  preserveSystemTags?: boolean;
  deleteTag?: (vl: ITag) => void;
  noPopUp?: boolean;
}

export default function Tag(props: Props): JSX.Element | null {
  const {
    tag,
    deleteTag,
    customStyle,
    isLayout,
    tooltip,
    preserveSystemTags,
    disabled,
    noPopUp,
  } = props;

  const nanoId = nanoid();

  const getCampaignTypeIcon = useMemo(() => {
    return (
      <>
        {tag.isStartTag && (
          <BadgeContainer className="isStart">
            <StartCampaignTagIcon width={11} height={20} color="#FB972E" />
          </BadgeContainer>
        )}
        {tag.isStopTag && (
          <BadgeContainer className="isStop">
            <img src={"./icons/stop-card.svg"} alt="stop" />
          </BadgeContainer>
        )}
      </>
    );
  }, [tag.isStartTag, tag.isStopTag]);

  if (!tag) return null;

  return (
    <Container
      style={customStyle}
      layout={isLayout}
      type={tag.type!}
      data-tip
      data-for={nanoId || tag._id}
      id="tag-container"
    >
      {getCampaignTypeIcon}
      <TextContainer>{tag.name}</TextContainer>
      {!noPopUp && (
        <ReactTooltip
          id={nanoId || tag._id}
          place="top"
          effect="solid"
          backgroundColor="#333333"
          css={customTooltip}
        >
          <TooltipText>
            {tooltip || `${tag.category} -> ${tag.name}`}
          </TooltipText>
        </ReactTooltip>
      )}
      {deleteTag &&
        !(preserveSystemTags && tag.type === ICategoryType.System) &&
        !disabled && (
          <Icon onClick={() => deleteTag(tag)}>
            <IconBerserkerMail name="close-orange" width={9} height={9} />
          </Icon>
        )}
    </Container>
  );
}
